/**
 * Folding a profile's functions into the modules they came from.
 *
 * A flat top-200 answers "which function was slow". Grouped, the same data
 * answers "which dependency was slow", which is the question an operator
 * looking at a cold start actually has — import cost is spread across
 * hundreds of functions in one package and is invisible one row at a time.
 *
 * Only own time is summed. Cumulative time counts a caller and everything it
 * called, so adding it across siblings would count nested work twice and
 * produce group totals larger than the run itself.
 */
import type { TaskProfileFunction } from '@optum/api-client/types';

export interface ModuleGroup {
  module: string;
  functions: TaskProfileFunction[];
  /** Additive across functions, unlike cumulative time. */
  ownMs: number;
  calls: number;
}

const VENDORED = /(?:^|\/)(?:site-packages|dist-packages)\//;
const BUILT_IN = 'built-in';

/** The leading total of pstats' `total/primitive` count. */
export function callTotal(calls: string): number {
  return Number.parseInt(calls, 10) || 0;
}

/**
 * The module a profiled frame belongs to.
 *
 * Third-party frames are attributed to their distribution — the segment
 * straight after `site-packages`, which is the name an operator would
 * recognise. Everything else is attributed to its own directory, because the
 * leading segments of an absolute path are the machine's, not the program's.
 */
export function moduleOf(file: string): string {
  if (!file || file === '~') return BUILT_IN;
  // pstats reports built-ins and frozen imports in angle brackets; they are
  // already the most specific name available.
  if (file.startsWith('<')) return file;

  const path = file.replace(/\\/g, '/');
  const vendored = VENDORED.test(path);
  const relevant = vendored ? (path.split(VENDORED).pop() ?? path) : path;
  const segments = relevant.split('/').filter(Boolean);

  if (segments.length === 0) return file;
  if (segments.length === 1) return segments[0]!.replace(/\.py$/, '');
  return vendored ? segments[0]! : segments[segments.length - 2]!;
}

/**
 * Group by module, each group's functions ordered by cumulative time.
 *
 * Groups are returned in descending own time — the order in which they cost
 * the run something.
 */
export function groupByModule(functions: TaskProfileFunction[]): ModuleGroup[] {
  const groups = new Map<string, ModuleGroup>();

  for (const fn of functions) {
    const module = moduleOf(fn.file);
    const group = groups.get(module) ?? { module, functions: [], ownMs: 0, calls: 0 };
    group.functions.push(fn);
    group.ownMs += fn.own_ms;
    group.calls += callTotal(fn.calls);
    groups.set(module, group);
  }

  for (const group of groups.values()) {
    group.functions.sort((a, b) => b.cumulative_ms - a.cumulative_ms);
  }
  return [...groups.values()].sort((a, b) => b.ownMs - a.ownMs);
}
